/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C2b §3] THE BOUNDS ON AN UPLOADED FILE. `constants/`, NOT `schema/`.
 *
 *  ⚠️ THEY ARE HERE FOR `pwa`'s REASON, AND FOR A SECOND ONE. §8's rule is that a runtime
 *  value a client needs comes from `@pams/shared/constants`, which imports neither zod nor
 *  luxon. The second reason is stronger: the SIZE CAP is enforced by `busboy`, which reads it
 *  before any schema exists — a multipart body is never `res.locals.validated`, so a bound
 *  living in a zod schema could not be applied to the thing it bounds.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ PDF ONLY, AND IT IS AN ALLOW-LIST OF ONE.
 *
 * §3 asks for PDF attachments. An allow-list rather than a deny-list for the obvious reason,
 * and ONE member rather than "documents" because every addition is a decision about what this
 * system will serve back to a browser: an SVG is a script, an HTML file is a script, and an
 * Office document is a macro host. A PDF served with `Content-Disposition: attachment` and
 * `X-Content-Type-Options: nosniff` is inert.
 *
 * ⚠️ THE CLIENT'S CLAIMED TYPE IS NOT TRUSTED. This is checked against the declared MIME AND
 * against the file's own first bytes (`%PDF-`) — a `Content-Type` header is whatever the
 * uploader typed, and a renamed executable arrives with any type its sender likes.
 */
export const ATTACHMENT_CONTENT_TYPES = ['application/pdf'] as const;
export type AttachmentContentType = (typeof ATTACHMENT_CONTENT_TYPES)[number];

/** The first five bytes of every PDF. Checked in the stream, not after it. */
export const PDF_MAGIC = '%PDF-';

/**
 * ⚠️ 5 MB, AND IT MUST MOVE TOGETHER WITH `client_max_body_size` IN BOTH NGINX CONFIGS.
 *
 * nginx defaults to **1 MB** and returns `413` before Express ever sees the request — so a
 * cap declared only here would be enforced at a number nobody chose, on the same-origin
 * `/api` proxy path, with the API's own limit never reached. `docker/nginx.conf` and
 * `docker/nginx.pwa.conf` both set it, and `docs/ops/uploads.md` §2 records the pair.
 *
 * ⚠️ AND IT IS NOT `BODY_LIMIT`. That governs the JSON parser, which never sees a multipart
 * body; raising one does nothing for the other, and conflating them is how a 5 MB PDF ends up
 * refused with a message about JSON.
 */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

/** At most this many files on one order. A bound, so no order can be a file store. */
export const ATTACHMENT_MAX_PER_ARRANGEMENT = 10;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3b §6] THE LONGEST FILENAME A NEW UPLOAD KEEPS — 32 CHARACTERS **INCLUDING** THE EXTENSION.
 *
 *  Scanner and phone filenames arrive as things like
 *  `WhatsApp Image 2026-08-15 at 19.42.11 (1).pdf`, which is unreadable in a list, in a
 *  dropdown and in a `Content-Disposition` header alike. `sanitiseFilename` truncates to this,
 *  reserving the extension INSIDE the cap — `slice(0, MAX - ext.length) + ext` — so the result
 *  is never longer than the number, which is what makes "32" mean what an operator would guess.
 *
 *  ⚠️ IT IS **NOT** THE MODEL'S `maxlength`. See `ATTACHMENT_STORED_FILENAME_MAX` below: this
 *  one governs what is WRITTEN from now on, and lowering the storage bound with it would refuse
 *  to save rows that are already stored.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ATTACHMENT_MAX_FILENAME = 32;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3b §6] THE STORAGE BOUND, WHICH DID **NOT** MOVE WITH THE CAP ABOVE — AND THE REASON IS
 *  MEASURED, NOT CAUTIOUS.
 *
 *  Both file models declare `maxlength: <this>` on `filename`. Mongoose re-validates a hydrated
 *  document's INIT-STATE paths on `save()` — `_getPathsToValidate` includes
 *  `activePaths.getStatePaths('init')` — so lowering the model's bound to 32 would make **a
 *  title-only edit of an existing uploaded order answer 422**, naming a filename the operator
 *  never touched and cannot shorten from that screen. Every row filed before this pass has a
 *  name of up to 120.
 *
 *  So the two numbers are two different questions: what a NEW upload is normalised to, and what
 *  the collection will still accept from a row that is already in it. They are allowed to
 *  converge the day something backfills the stored names; until then, one constant would have
 *  been one number answering two questions, which is the shape this repository keeps correcting.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ATTACHMENT_STORED_FILENAME_MAX = 120;
